import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { CreditCard, Calendar, AlertTriangle, CheckCircle, XCircle, Pause, Play, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MySubscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userEmail, setUserEmail] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);

  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    const emailFromStorage = localStorage.getItem('user_email');
    const email = emailFromUrl || emailFromStorage;
    
    if (email) {
      setUserEmail(email);
      fetchSubscription(email);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchSubscription = async (email) => {
    try {
      const response = await axios.get(`${API}/users/${email}/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/users/${userEmail}/subscription/cancel`);
      setShowCancelModal(false);
      fetchSubscription(userEmail);
      alert('Your subscription has been cancelled.');
    } catch (error) {
      alert('Failed to cancel subscription. Please try again or contact support.');
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePauseSubscription = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/users/${userEmail}/subscription/pause`);
      setShowPauseModal(false);
      fetchSubscription(userEmail);
      alert('Your subscription has been paused.');
    } catch (error) {
      alert('Failed to pause subscription. Please try again or contact support.');
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/users/${userEmail}/subscription/resume`);
      fetchSubscription(userEmail);
      alert('Your subscription has been resumed.');
    } catch (error) {
      alert('Failed to resume subscription. Please try again or contact support.');
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8 text-center max-w-md mx-auto">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading subscription details...</p>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (!userEmail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8 text-center max-w-md mx-auto">
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Authentication Required</h1>
            <p className="text-gray-600 mb-6">Please log in to view your subscription.</p>
            <Button onClick={() => navigate('/login')} className="bg-red-600 hover:bg-red-700 text-white">
              Go to Login
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'adhoc': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanLabel = (plan) => {
    switch (plan) {
      case 'monthly': return 'Monthly ($299/month)';
      case 'yearly': return 'Yearly ($2,990/year)';
      case 'adhoc': return 'Adhoc ($49 per job)';
      default: return plan || 'N/A';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-[#1e3a5f] text-white py-8">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/carrier-dashboard')}
            className="text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">My Subscription</h1>
          <p className="text-gray-300">{userEmail}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {subscription ? (
          <>
            {/* Current Plan Card */}
            <Card className="p-6 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Current Plan</h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(subscription.subscription_status)}`}>
                    {subscription.subscription_status?.toUpperCase() || 'NO SUBSCRIPTION'}
                  </span>
                </div>
                <CreditCard className="w-10 h-10 text-[#1e3a5f]" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Plan Type</p>
                  <p className="text-lg font-semibold">{getPlanLabel(subscription.subscription_plan)}</p>
                </div>

                {subscription.subscription_expiry && subscription.subscription_plan !== 'adhoc' && (
                  <div>
                    <p className="text-sm text-gray-600">
                      {subscription.subscription_status === 'cancelled' ? 'Access Until' : 'Next Billing Date'}
                    </p>
                    <p className={`text-lg font-semibold ${
                      new Date(subscription.subscription_expiry) < new Date() ? 'text-red-600' : ''
                    }`}>
                      {new Date(subscription.subscription_expiry).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                      {new Date(subscription.subscription_expiry) < new Date() && ' (Expired)'}
                    </p>
                  </div>
                )}

                {subscription.subscription_plan === 'adhoc' && (
                  <div>
                    <p className="text-sm text-gray-600">Payment Model</p>
                    <p className="text-lg font-semibold">Pay $49 per accepted job</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-600">Carrier Status</p>
                  <p className="flex items-center gap-2">
                    {subscription.approved_carrier ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-semibold">Approved</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        <span className="text-yellow-700 font-semibold">Pending Approval</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Promo Codes Applied */}
              {subscription.promo_codes_applied && subscription.promo_codes_applied.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-2">Promo Codes Applied</p>
                  <div className="flex flex-wrap gap-2">
                    {subscription.promo_codes_applied.map((code, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Subscription Actions */}
            {subscription.subscription_status === 'active' && subscription.subscription_plan !== 'adhoc' && (
              <Card className="p-6 mb-6">
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-4">Manage Subscription</h3>
                
                <div className="space-y-4">
                  {subscription.can_cancel && (
                    <>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold">Pause Subscription</p>
                          <p className="text-sm text-gray-600">Temporarily pause your billing. You won't be able to quote during this time.</p>
                        </div>
                        <Button 
                          variant="outline"
                          onClick={() => setShowPauseModal(true)}
                          disabled={actionLoading}
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </Button>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-red-700">Cancel Subscription</p>
                          <p className="text-sm text-red-600">Cancel your subscription. You'll have access until the end of your billing period.</p>
                        </div>
                        <Button 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-100"
                          onClick={() => setShowCancelModal(true)}
                          disabled={actionLoading}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* Adhoc Info */}
            {subscription.subscription_plan === 'adhoc' && (
              <Card className="p-6 mb-6 bg-blue-50 border-blue-200">
                <h3 className="text-lg font-bold text-blue-800 mb-2">Adhoc Membership</h3>
                <p className="text-blue-700">
                  As an adhoc member, you can quote on any load for free. You only pay $49 when your quote is accepted and the job is confirmed.
                </p>
                <p className="text-blue-700 mt-2">
                  Want unlimited quoting? <button onClick={() => navigate('/signup?role=carrier')} className="underline font-semibold">Upgrade to a subscription plan</button>
                </p>
              </Card>
            )}

            {/* Cancelled/Expired Info */}
            {(subscription.subscription_status === 'cancelled' || subscription.subscription_status === 'expired') && (
              <Card className="p-6 mb-6 bg-yellow-50 border-yellow-200">
                <h3 className="text-lg font-bold text-yellow-800 mb-2">
                  {subscription.subscription_status === 'cancelled' ? 'Subscription Cancelled' : 'Subscription Expired'}
                </h3>
                <p className="text-yellow-700 mb-4">
                  {subscription.subscription_status === 'cancelled' 
                    ? "Your subscription has been cancelled. You can resubscribe at any time."
                    : "Your subscription has expired. Please renew to continue quoting on loads."
                  }
                </p>
                <Button 
                  onClick={() => navigate('/signup?role=carrier')}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  Resubscribe
                </Button>
              </Card>
            )}

            {/* Contact Support */}
            <Card className="p-6">
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">Need Help?</h3>
              <p className="text-gray-600 mb-4">
                If you have questions about your subscription or need assistance, please contact our support team.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:info@trucknsendit.com" className="text-blue-600 hover:underline">
                  info@trucknsendit.com
                </a>
                <a href="tel:1300380700" className="text-blue-600 hover:underline">
                  1300 380 700
                </a>
              </div>
            </Card>
          </>
        ) : (
          <Card className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">No Subscription Found</h2>
            <p className="text-gray-600 mb-6">You don't have an active subscription yet.</p>
            <Button 
              onClick={() => navigate('/signup?role=carrier')}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Sign Up as Carrier
            </Button>
          </Card>
        )}
      </div>

      {/* Cancel Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You'll retain access until the end of your current billing period."
        confirmText="Yes, Cancel"
        cancelText="Keep Subscription"
        variant="danger"
      />

      {/* Pause Modal */}
      <ConfirmModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={handlePauseSubscription}
        title="Pause Subscription"
        message="Are you sure you want to pause your subscription? You won't be charged during the pause, but you also won't be able to quote on loads."
        confirmText="Yes, Pause"
        cancelText="Keep Active"
        variant="warning"
      />

      <Footer />
    </div>
  );
};

export default MySubscription;
